import { useState, useEffect, useRef, useMemo, use } from "react";
import { useDispatch, useSelector } from "react-redux";
import Product from "./ProductViews/Product.jsx";
import ProductGridView from "./ProductViews/ProductGridView.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faThLarge, faThList } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { FaSpinner } from "react-icons/fa";
import ScrollToTopOnMount from "./ScrollToTopOnMount.js";
import FilterMenuLeft from "./FilterMenuLeft.jsx";
import NoProductsFound from "./ProductViews/NoProductsFound.jsx";

// Redux slices
import { fetchSearchResults } from "../../../redux/features/searchProductsSlice";
import { addToCart, updateCart } from "../../../redux/features/cartSlice";
import { addToWhishlist } from "../../../redux/features/addToWhishlistSlice";
import { removeWishItem } from "../../../redux/features/getWishlistSlice";
import { fetchCategories } from "../../../redux/features/searchCategorySlice.js";
import { toast } from "react-toastify";
import { Box, Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "./useIsMobile.js";
import { Modal } from "react-bootstrap";

function ProductList({ className, ...props }) {
  const location = useLocation();
  const { ref: productListRef, ...rest } = props;
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useIsMobile();
  const [loadingFilter, setLoadingFilter] = useState(false);

  const [viewType, setViewType] = useState({ grid: true });
  const [isAnimating, setIsAnimating] = useState(false);
  const [cartProducts, setCartProducts] = useState({});
  const [wishlistedProducts, setWishlistedProducts] = useState({});
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { categories, childCategories } = useSelector((state) => state.categories);

  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const sliderRef = useRef(null);

  //max min prices states

  // Redux state
  const lang = useSelector((state) => state.language.lang);
  const { api_token } = useSelector((state) => state.auth);
  const {
    isSearching,
    searchQuery,
    category_id,
    parent_category_id,
    searchResults,
    loading,
    currentPage,
    total,
    max_price,
    min_price,
  } = useSelector((state) => state.search);

  // price states
  const [minPriceState, setMinPriceState] = useState(min_price || 1);
  const [maxPriceState, setMaxPriceState] = useState(max_price || 5000);

  useEffect(() => {
    if (min_price) setMinPriceState(Number(min_price));
    if (max_price) setMaxPriceState(Number(max_price));
  }, [min_price, max_price]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const payload = {
      searchQuery: params.get("query")?.trim() || "",
      page: 0,
      lang,
      min_price: params.get("min_price") || "",
      max_price: params.get("max_price") || "",
      filters: {
        delivery_days: params.get("delivery_days"),
        min_price: params.get("min_price"),
        max_price: params.get("max_price"),
        sort: params.get("sort"),
      }
    };

    if (params.get("category_id")) {
      payload.category_id = params.get("category_id");
    }

    if (params.get("parent_category_id")) {
      payload.parent_category_id = params.get("parent_category_id");
    }

    dispatch(fetchSearchResults(payload));
  }, [location.search, lang]);

  // 🟢 Handle child category click and fetch products
  const handleChildCategoryClick = async (categoryId) => {
    setSelectedChildCategory(categoryId);

    const newParams = new URLSearchParams(window.location.search);
    newParams.set("category_id", categoryId);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newParams.toString()}`
    );

    // Fetch child categories
    const previousChildren = [...childCategories];
    dispatch({ type: "categories/clearChildCategories" });

    const resultAction = await dispatch(
      fetchCategories({ lang, parent_id: categoryId })
    );

    if (fetchCategories.fulfilled.match(resultAction)) {
      const nextChildren = resultAction.payload;
      if (!nextChildren || nextChildren.length === 0) {
        dispatch({
          type: "categories/setChildCategories",
          payload: previousChildren,
        });
      }
    } else {
      console.error("Failed to fetch child categories:", resultAction.payload);
      dispatch({
        type: "categories/setChildCategories",
        payload: previousChildren,
      });
    }

    // 🟢 Immediately fetch products for the selected category
    const payload = {
      searchQuery: searchQuery || "",
      page: 0,
      min_price,
      max_price,
      filters: { min_price, max_price },
      category_id: categoryId,
      parent_category_id: newParams.get("parent_category_id") || null,
      lang,
    };

    dispatch(fetchSearchResults(payload));
  };

  // 🟢 Slider scroll controls
  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 200;
      sliderRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Fetch products on page load / query change
  // useEffect(() => {
  //   console.log('Fetch products on page load / query change useEffect called!');
  //   const params = new URLSearchParams(location.search); // 👈 use location.search
  //   const query = params.get("query") ? decodeURIComponent(params.get("query").trim()) : "";
  //   const category_id = params.get("category_id");
  //   const parent_category_id = params.get("parent_category_id");
  //   const delivery_days = params.get("delivery_days");
  //   const min_price = params.get("min_price");
  //   const max_price = params.get("max_price");
  //   const sort = params.get("sort");

  //   const payload = {
  //     searchQuery: query,
  //     page: 0,
  //     min_price,
  //     max_price,
  //     filters: { delivery_days, min_price, max_price, sort },
  //     lang,
  //   };

  //   if (category_id) payload.category_id = category_id;
  //   if (parent_category_id) payload.parent_category_id = parent_category_id;

  //   dispatch(fetchSearchResults(payload));
  // }, [dispatch, location.search, lang]);

  // Animate when new search data loads
  useEffect(() => {
    if (searchQuery) setIsAnimating(true);
  }, [searchQuery, currentPage]);

  const [loadingMore, setLoadingMore] = useState(false);

  // Infinite scroll to fetch more products
  useEffect(() => {
    if (!searchResults || searchResults.length === 0) return;

    const handleScroll = async () => {
      const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
      const bottomThreshold = document.documentElement.offsetHeight - 600;

      if (scrollPosition < bottomThreshold || loadingMore || searchResults.length >= total) return;

      setLoadingMore(true);

      // 🔍 Always re-read the *latest* URL state directly from window.location
      const params = new URLSearchParams(window.location.search);

      const query = params.get("query") ? decodeURIComponent(params.get("query").trim()) : "";
      const category_id = params.get("category_id") || null;
      const parent_category_id = params.get("parent_category_id") || null;
      const min_price = params.get("min_price");
      const max_price = params.get("max_price");

      const payload = {
        searchQuery: query,
        page: currentPage + 1,
        min_price,
        max_price,
        lang,
      };

      if (category_id) payload.category_id = category_id;
      if (parent_category_id) payload.parent_category_id = parent_category_id;

      // 🧩 Always ensure category context stays consistent
      await dispatch(fetchSearchResults(payload));

      setLoadingMore(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [searchResults, currentPage, total, loadingMore, lang, dispatch]);


  const handleAddToCart = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_cart")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      return;
    }

    try {
      setCartProducts((prev) => ({ ...prev, [productId]: true }));
      await dispatch(addToCart({ product_id: productId })).unwrap();
      toast.success(`${t("Product_added_to_your_cart_successfully")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',
      });
    } catch (error) {
      setCartProducts((prev) => ({ ...prev, [productId]: false })); // revert state
      toast.error(`${t("Failed_to_add_product_to_cart_Please_try_again")}.`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Add to Cart Error:", error);
    }
  };

  const handleUpdateCart = async (product_id, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await dispatch(updateCart({ product_id, quantity: newQuantity })).unwrap();
      toast.success(`${t("Cart_updated_successfully")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (error) {
      toast.error(`${t("Failed_to_update_cart")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Update Cart Error:", error);
    }
  };

  const handleAddToWishlist = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      return;
    }

    try {
      setWishlistedProducts((prev) => ({
        ...prev,
        [productId]: !prev[productId],
      }));

      await dispatch(addToWhishlist({ product_id: productId })).unwrap();
      toast.success(`${t("Product_added_to_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (error) {
      setWishlistedProducts((prev) => ({ ...prev, [productId]: false })); // revert
      toast.error(`${t("Failed_to_add_product_to_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Add to Wishlist Error:", error);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_remove_product_from_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      return;
    }

    try {
      setWishlistedProducts((prev) => ({ ...prev, [productId]: false }));
      await dispatch(removeWishItem(productId)).unwrap();
      toast.success(`${t("Product_removed_from_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (error) {
      setWishlistedProducts((prev) => ({ ...prev, [productId]: true })); // revert
      toast.error(`${t("Failed_to_remove_product_from_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Remove Wishlist Error:", error);
    }
  };

  const products = searchResults || [];

  // Highlight function (for search matches)
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.split(regex).map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? <span key={i} className="highlight">{part}</span>
        : part
    );
  };

  const currentCategoryName = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const currentParentCategoryId = params.get("parent_category_id");
    const currentCategoryId = params.get("category_id");

    if (currentCategoryId) {
      const foundCategory = categories.find(cat => cat.id === currentCategoryId);
      if (foundCategory) return foundCategory.title;
    }
    if (currentParentCategoryId) {
      const foundParentCategory = categories.find(cat => cat.id === currentParentCategoryId);
      if (foundParentCategory) return foundParentCategory.title;
    }
    return `${t('All_Products')}`;
  }, [window.location.search, categories]);

  return (
    <div ref={productListRef} className={className ? "pt-2 overflow-hidden" : "pt-2 overflow-hidden"}>
      <div className="container px-xl-4">
        <h3 className="text-start mb-4">{currentCategoryName}</h3>
        <ScrollToTopOnMount />

        {/* Filter + View Type */}
        <div className="d-flex justify-content-between align-items-start">
          <button
            className="btn filter-button align-items-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="me-2">{t("Filters")}</span>
            <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
          </button>

          <div
            className="d-flex py-1 px-2 rounded"
            style={{
              background: "var(--secondary-color)",
              borderRadius: "8px",
            }}
          >
            <button
              className="btn px-2 py-1"
              style={{
                background: viewType.grid ? "#FFFFFF" : "transparent",
                borderRadius: "6px",
              }}
              onClick={() => setViewType({ grid: true })}
            >
              <FontAwesomeIcon
                icon={faThList}
                style={{ color: "var(--secondary-light-color)" }}
              />
            </button>
            <button
              className="btn px-2 py-1"
              style={{
                background: !viewType.grid ? "#FFFFFF" : "transparent",
                borderRadius: "6px",
              }}
              onClick={() => setViewType({ grid: false })}
            >
              <FontAwesomeIcon
                icon={faThLarge}
                style={{ color: "var(--secondary-light-color)" }}
              />
            </button>
          </div>
        </div>

        <div className="row  py-2">
          {/* === Desktop Filter Sidebar === */}
          {!isMobile && showFilters && (
            <div className="col-lg-3">
              <div className="border rounded shadow-sm mb-2">
                <FilterMenuLeft
                  key={showFilters ? window.location.href : "closed"}
                  categories={categories}
                  selectedCategory={[]}
                  setSelectedCategory={() => { }}
                  minPrice={minPriceState}
                  maxPrice={maxPriceState}
                  setMinPrice={setMinPriceState}
                  setMaxPrice={setMaxPriceState}
                  applyFilters={() => { }}
                  showFilters={showFilters}
                />
              </div>
            </div>
          )}

          {/* === Mobile Filter Modal === */}
          {isMobile && (
            <Modal
              show={showFilters}
              onHide={() => setShowFilters(false)}
              centered
              size="lg"
            >

              <Modal.Header closeButton>
                <Modal.Title>{t("Filters")}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* 🔹 Overlay Spinner */}
                {loadingFilter && (
                  <div className="loading-overlay">
                    <FaSpinner size={40} color="var(--theme-primary)" className="spin" />
                  </div>
                )}

                <FilterMenuLeft
                  key={showFilters ? window.location.href : "closed"}
                  categories={categories}
                  selectedCategory={[]}
                  setSelectedCategory={() => { }}
                  minPrice={minPriceState}
                  maxPrice={maxPriceState}
                  setMinPrice={setMinPriceState}
                  setMaxPrice={setMaxPriceState}
                  applyFilters={() => setShowFilters(false)} // close modal on apply
                  showFilters={showFilters}
                  onClosePopup={() => setShowFilters(false)}
                  setLoadingFilter={setLoadingFilter}
                />
              </Modal.Body>
            </Modal>
          )}

          {/* Product List */}
          <div className={showFilters ? "col-lg-9" : "col-lg-12"}>
            <div className="container">
              {/* ✅ Show slider ONLY if childCategories exist */}
              {Array.isArray(childCategories) && childCategories.length > 0 && (
                <Box sx={{ position: "relative", px: 4 }}>
                  {/* Left Arrow */}
                  <Button
                    onClick={() => scroll("left")}
                    sx={{
                      position: "absolute",
                      left: -10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "var(--secondary-light-color)",
                      color: "#fff",
                      boxShadow: 2,
                      zIndex: 10,
                      "&:hover": {
                        bgcolor: "grey.100",
                        color: "var(--secondary-light-color)",
                      },
                    }}
                  >
                    <ChevronLeft />
                  </Button>

                  {/* Right Arrow */}
                  <Button
                    onClick={() => scroll("right")}
                    sx={{
                      position: "absolute",
                      right: -10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "var(--secondary-light-color)",
                      color: "#fff",
                      boxShadow: 2,
                      zIndex: 10,
                      "&:hover": {
                        bgcolor: "grey.100",
                        color: "var(--secondary-light-color)",
                      },
                    }}
                  >
                    <ChevronRight />
                  </Button>

                  {/* Category Slider */}
                  <Box
                    ref={sliderRef}
                    sx={{
                      display: "flex",
                      overflowX: "auto",
                      gap: 2,
                      py: 1,
                      px: 2,
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {childCategories.map((child) => (
                      <Button
                        key={child.id}
                        onClick={() => handleChildCategoryClick(child.id)}
                        sx={{
                          flex: "0 0 auto",
                          borderRadius: 3,
                          minWidth: 120,
                          bgcolor:
                            selectedChildCategory === child.id
                              ? "var(--secondary-light-color)"
                              : "#fff",
                          color:
                            selectedChildCategory === child.id
                              ? "#fff"
                              : "text.primary",
                          textTransform: "none",
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1,
                          boxShadow: selectedChildCategory === child.id ? 3 : 1,
                          transform:
                            selectedChildCategory === child.id
                              ? "scale(1.05)"
                              : "scale(1)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            bgcolor:
                              selectedChildCategory === child.id
                                ? "var(--secondary-light-color)"
                                : "grey.200",
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        {child.title}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </div>
            <div className={`row ${isAnimating ? "slide-in" : ""}`}>
              {isSearching ? (
                // Show full spinner during header search
                <div className="d-flex align-items-center justify-content-center py-5 w-100">
                  <FaSpinner size={35} color="var(--theme-primary)" className="spin" />
                </div>
              ) : products.length > 0 ? (
                <>
                  <div
                    className={`row cols-1 cols-md-2 cols-lg-2 g-1 g-md-3 flex-shrink-0 px-2 px-sm-0 position-relative ${viewType.grid ? "cols-xl-4" : "cols-xl-2"}`}
                  >
                    {products.map((product) => {
                      const imageSrc = Array.isArray(product.image)
                        ? product.image[0]
                        : typeof product.image === "string"
                          ? product.image
                          : "https://royshop.in/assets/no-image.png";

                      const highlightedProduct = {
                        ...product,
                        name: highlightText(product.title || "", searchQuery || ""),
                        description: highlightText(product.description || "", searchQuery || ""),
                        brand: { name: highlightText(product.brand_name || "Unknown", searchQuery || "") },
                        price: product.sales_price || 0,
                        image: imageSrc,
                      };

                      const commonProps = {
                        showFilters,
                        product: highlightedProduct,
                        percentOff: product.discount || null,
                        image: imageSrc,
                        onAddToCart: () => handleAddToCart(product.id),
                        onAddToWishlist: () => handleAddToWishlist(product.id),
                        onRemoveWishlist: () => handleRemoveFromWishlist(product.id),
                        inWishlist: wishlistedProducts[product.id] || product?.in_whishlist || false,
                        inCart: cartProducts[product.id] || product.in_cart || false,
                      };

                      return viewType.grid ? (
                        <Product key={product.id} {...commonProps} />
                      ) : (
                        <ProductGridView key={product.id} {...commonProps} />
                      );
                    })}
                  </div>

                  {/* Infinite scroll loader at bottom */}
                  {loading && !isSearching && (
                    <div className="d-flex align-items-center justify-content-center py-3 w-100">
                      <FaSpinner size={35} color="var(--theme-primary)" className="spin" />
                    </div>
                  )}
                </>
              ) : (
                <NoProductsFound />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ProductList;
